import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import RejectBookingModal from "./RejectBookingModal";
import { useTranslation } from "react-i18next";

const disabledBlack = {
  "& .MuiFormLabel-root.Mui-disabled": {
    color: "black", // Cambia el color de la etiqueta cuando está deshabilitado
  },
  "& .MuiInputBase-root.Mui-disabled": {
    "& input": {
      WebkitTextFillColor: "black", // Para navegadores basados en Webkit como Chrome y Safari
      color: "black", // Para otros navegadores
    },
  },
};

interface AcceptOrRejectBookingInterface {
  id?: number;
  name: string;
  creationDate?: string;
  associatedNumber?: string;
  phone?: string;
  email?: string;
  message?: string;
}

interface DialogProps {
  actionButton?: any;
  data: any;
  onSubmit: any;
  cancelModal: any;
  modalType?: any;
}

const AcceptOrRejectBookingModal = ({
  data,
  onSubmit,
  cancelModal,
}: DialogProps) => {
  const { t } = useTranslation();
  //Validación de datos
  const validation = Yup.object().shape({
    message: Yup.string().required(t("fieldRequired")),
  });

  const parseStops = (stopsString: string): string[] => {
    return stopsString ? stopsString.split("|") : [];
  };
  const name = data.name ? data.name : "";
  const lastName = data.lastName ? data.lastName : "";
  const fullName = `${name} ${lastName}`;
  const isTransportation = data.type === "transportation";

  const defaultValues = {
    id: data.id ? data.id : 0,
    name: fullName,
    associatedNumber: data.associatedNumber ? data.associatedNumber : "",
    phone: data.phone ? data.phone : "",
    email: data.email ? data.email : "",
    creationDate: data.creationDate ? data.creationDate.substring(0, 10) : "",
    stops: data.stops ? parseStops(data.stops) : [],
    observations: data.observations ? data.observations : t("noObservations"),
    paymentMethod: data.payMethod,
    type: data.type
      ? isTransportation
        ? t("transportation")
        : t("installation")
      : t("notDefined"),
    pickUpLocation: data.pickUpLocation ? data.pickUpLocation : t("notDefined"),
    dropOffLocation: data.dropOffLocation
      ? data.dropOffLocation
      : t("notDefined"),
    airline: data.airline ? data.airline : t("notDefined"),
    flightNumber: data.flightNumber ? data.flightNumber : t("notDefined"),
    arrivalTime: data.arrivalTime ? data.arrivalTime : t("notDefined"),
  };
  const [openModalReject, setOpenModalReject] = useState(false);

  const handleCancelModalReject = () => {
    setOpenModalReject(false);
  };

  const {
    register,
    formState: { errors },
    control,
  } = useForm<AcceptOrRejectBookingInterface>({
    defaultValues,
    resolver: yupResolver(validation),
    mode: "onChange",
  });

  const formatSingleHourTo12Hour = (time: string) => {
    const [hour, minutes] = time.split(":");

    let formattedHour = parseInt(hour, 10);
    let period = "AM";

    if (formattedHour === 0) {
      formattedHour = 12;
    } else if (formattedHour === 12) {
      period = "PM";
    } else if (formattedHour > 12) {
      formattedHour -= 12;
      period = "PM";
    }

    return `${formattedHour}:${minutes} ${period}`;
  };

  return (
    <>
      <DialogTitle id="alert-dialog-title" sx={{ pt: 4, px: 4 }}>
        {t("bookings")}
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 0 }}>
        <form>
          <Grid container spacing={3} maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
            <Grid
              item
              xs={12}
              sx={{
                pt: 4,
                px: 4,
                marginBottom: "10px",
              }}
            >
              <Typography
                sx={{
                  textDecoration: "underline",
                  fontSize: "18px",
                }}
              >
                <b>{t("reservationData")}</b>
              </Typography>
            </Grid>
            <Grid container xs={12} spacing={3} paddingX="20px">
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("type")}
                  type="text"
                  variant="standard"
                  value={defaultValues.type}
                  autoComplete="name3"
                  sx={disabledBlack}
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("reservationN°")}
                  type="text"
                  variant="standard"
                  value={data.id}
                  autoComplete="name3"
                  sx={disabledBlack}
                  {...register("id")}
                  error={errors.id && Boolean(errors.id)}
                  helperText={errors.id && errors.id.message}
                  disabled
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("paymentMethod")}
                  type="text"
                  variant="standard"
                  value={data.payMethod}
                  autoComplete="name3"
                  sx={disabledBlack}
                  disabled
                />
              </Grid>

              <Grid item xs={4}>
                <Controller
                  name={"creationDate"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("areaRequestDate")}
                      type="text"
                      variant="standard"
                      value={value}
                      autoComplete="name3"
                      sx={disabledBlack}
                      {...register("creationDate")}
                      error={
                        errors.creationDate && Boolean(errors.creationDate)
                      }
                      helperText={
                        errors.creationDate && errors.creationDate.message
                      }
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name={"associatedNumber"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("associateNumber")}
                      type="text"
                      variant="standard"
                      value={value}
                      autoComplete="name3"
                      sx={disabledBlack}
                      {...register("associatedNumber")}
                      error={
                        errors.associatedNumber &&
                        Boolean(errors.associatedNumber)
                      }
                      helperText={
                        errors.associatedNumber &&
                        errors.associatedNumber.message
                      }
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name={"phone"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("phone")}
                      type="text"
                      variant="standard"
                      value={value}
                      autoComplete="name3"
                      sx={disabledBlack}
                      {...register("phone")}
                      error={errors.phone && Boolean(errors.phone)}
                      helperText={errors.phone && errors.phone.message}
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={"name"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("name")}
                      type="text"
                      variant="standard"
                      value={value}
                      autoComplete="name3"
                      sx={disabledBlack}
                      {...register("name")}
                      error={errors.name && Boolean(errors.name)}
                      helperText={errors.name && errors.name.message}
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={"email"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label={t("email")}
                      type="text"
                      variant="standard"
                      value={value}
                      autoComplete="name3"
                      sx={disabledBlack}
                      {...register("email")}
                      error={errors.email && Boolean(errors.email)}
                      helperText={errors.email && errors.email.message}
                      disabled
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography
                sx={{
                  textDecoration: "underline",
                  fontSize: "18px",
                  marginBottom: "10px",
                }}
              >
                <b>
                  {t("reservationDetails")} {data.areaName}
                </b>
              </Typography>
            </Grid>
            <Grid container xs={12} spacing={4} paddingX="20px">
              <Grid item sm={6} xs={12}>
                <TextField
                  label={t("reservationDate")}
                  type="text"
                  variant="standard"
                  size="small"
                  fullWidth
                  sx={disabledBlack}
                  name="bookingDay"
                  value={data.bookingDay.substring(0, 10) || ""}
                  disabled
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  label={t("totalHours")}
                  type="text"
                  variant="standard"
                  size="small"
                  fullWidth
                  name="totalHours"
                  sx={disabledBlack}
                  value={data.totalHours || ""}
                  disabled
                />
              </Grid>

              {data.payMethod !== "ADMIN" && (
                <Grid item sm={6} xs={12}>
                  <TextField
                    label={t("areaAmount")}
                    variant="standard"
                    size="small"
                    fullWidth
                    type="text"
                    name="areaAmount"
                    sx={disabledBlack}
                    value={data.areaAmount || ""}
                    disabled
                  />
                </Grid>
              )}
              {data.spaces && (
                <Grid item sm={6} xs={12}>
                  <TextField
                    label={t("reservedSpaces")}
                    variant="standard"
                    size="small"
                    fullWidth
                    type="text"
                    name="reservedSpaces"
                    sx={disabledBlack}
                    value={data.spaces || ""}
                    disabled
                  />
                </Grid>
              )}
              <Grid item sm={6} xs={12}>
                <TextField
                  label={isTransportation ? t("departureTime") : t("startTime")}
                  type="text"
                  variant="standard"
                  size="small"
                  fullWidth
                  name="startDate"
                  sx={disabledBlack}
                  value={formatSingleHourTo12Hour(data.startTime) || ""}
                  disabled
                />
              </Grid>
              {!isTransportation && (
                <Grid item sm={6} xs={12}>
                  <TextField
                    label={t("endTime")}
                    type="text"
                    variant="standard"
                    size="small"
                    sx={disabledBlack}
                    fullWidth
                    name="endDate"
                    value={formatSingleHourTo12Hour(data.endTime) || ""}
                    disabled
                  />
                </Grid>
              )}
            </Grid>
            {isTransportation && (
              <Grid container xs={12} spacing={3} sx={{ pt: 4, px: 5 }}>
                <Grid item xs={12}>
                  <TextField
                    label={t("pickUpLocation")}
                    type="text"
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={disabledBlack}
                    name="pickUpLocation"
                    value={defaultValues.pickUpLocation}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("dropOffLocation")}
                    type="text"
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={disabledBlack}
                    name="dropOffLocation"
                    value={defaultValues.dropOffLocation}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("airline")}
                    type="text"
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={disabledBlack}
                    name="airline"
                    value={defaultValues.airline}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("flightNumber")}
                    type="text"
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={disabledBlack}
                    name="flightNumber"
                    value={defaultValues.flightNumber}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("arrivalTime")}
                    type="text"
                    variant="standard"
                    size="small"
                    fullWidth
                    sx={disabledBlack}
                    name="arrivalTime"
                    value={formatSingleHourTo12Hour(data.arrivalTime) || ""}
                    disabled
                  />
                </Grid>
              </Grid>
            )}
            {defaultValues.stops && defaultValues.stops.length > 0 && (
              <Grid item xs={12} sx={{ pt: 4, px: 4 }}>
                <Typography
                  sx={{
                    textDecoration: "underline",
                    fontSize: "18px",
                    marginBottom: "10px",
                  }}
                >
                  <b>{t("stops")}</b>
                </Typography>
                <Grid container spacing={4} paddingX="8px">
                  {defaultValues.stops.map((stop: string, index: number) => (
                    <Grid item sm={6} xs={12} key={index}>
                      <TextField
                        label={`${t("stop")} ${index + 1}`}
                        type="text"
                        variant="standard"
                        size="small"
                        fullWidth
                        sx={disabledBlack}
                        name={`stops[${index}]`}
                        value={stop}
                        disabled
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} sx={{ pt: 4, px: 4 }}>
              <Typography
                sx={{
                  textDecoration: "underline",
                  fontSize: "18px",
                  marginBottom: "8px",
                }}
              >
                <b>{t("observations")}</b>
              </Typography>
              <Grid container paddingX="8px">
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  variant="standard"
                  value={defaultValues.observations}
                  autoComplete="name3"
                  sx={disabledBlack}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ pb: 4, px: 4 }}>
        {/* {data.status === "APR" || data.status === "REJ" ? (
          <Button
            variant="contained"
            color="secondary"
            sx={{
              mt: 2,
              mr: 2,
            }}
            onClick={cancelModal}
          >
            Cancelar
          </Button>
        ) : (
          <> */}
        {data.status !== 3 && (
          <Button
            variant="contained"
            color="primary"
            sx={{
              mt: 2,
              mr: 2,
            }}
            onClick={() =>
              onSubmit({
                id: data.id,
                bookingDetailList: data.bookingDetailList,
                status: true,
              })
            }
          >
            {t("approve")}
          </Button>
        )}

        {data.status !== 4 && (
          // {data.status !== "REJ" && (

          <Button
            variant="contained"
            color="secondary"
            sx={{
              mt: 2,
              mr: 2,
            }}
            onClick={() => setOpenModalReject(true)}
          >
            {t("reject")}
          </Button>
        )}

        <Button
          variant="contained"
          color="secondary"
          sx={{
            mt: 2,
            mr: 2,
          }}
          onClick={cancelModal}
        >
          {t("cancel")}
        </Button>
        {/* </>
        )} */}
      </DialogActions>
      <RejectBookingModal
        openModalReject={openModalReject}
        handleCancelModalReject={handleCancelModalReject}
        onSubmit={onSubmit}
        data={data}
      />
    </>
  );
};

export default AcceptOrRejectBookingModal;
